import { useState, useEffect } from "react";
import { Link, useMatch, useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import InputWrapper from "@common/components/InputWrapper";
import logoImage from "@assets/images/learningportal.svg";
import { useLoginMutation } from "@features/auth/authApi";
import useAuthAdmin from "@common/hooks/useAuthAdmin";
import useAuthStudent from "@common/hooks/useAuthStudent";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const navigate = useNavigate();

  const [login, { isLoading, error }] = useLoginMutation();

  const match = useMatch("/admin/*");

  const isAuthAdmin = useAuthAdmin();
  const isAuthStudent = useAuthStudent();

  const resetForm = () => {
    setEmail("");
    setPassword("");
  };

  useEffect(() => {
    if (error?.data) {
      toast.error(error.data);
    }

    if (match !== null && isAuthAdmin) {
      navigate("/admin");
    }

    if (match === null && isAuthStudent) {
      navigate("/course");
    }
  }, [match, isAuthAdmin, isAuthStudent, error, navigate]);

  const handleAdminSubmit = async (e) => {
    e.preventDefault();
    const response = await login({ email, password });
    if (response?.data) {
      resetForm();
      navigate("/admin");
      toast.success("Admin logged in successfully!");
    }
  };

  const handleStudentSubmit = async (e) => {
    e.preventDefault();
    const response = await login({ email, password });
    if (response?.data) {
      resetForm();
      navigate("/course");
      toast.success("Student logged in successfully!");
    }
  };

  return (
    <section className="py-6 bg-primary h-screen grid place-items-center">
      <div className="mx-auto max-w-md px-5 lg:px-0">
        <div>
          <img className="h-12 mx-auto" src={logoImage} alt="logo" />
          <h2 className="mt-6 text-center text-3xl font-extrabold text-slate-100">
            Sign in to {match !== null ? "Admin" : "Student"} Account
          </h2>
        </div>
        <form
          className="mt-8 space-y-6"
          onSubmit={match !== null ? handleAdminSubmit : handleStudentSubmit}
        >
          <input type="hidden" name="remember" value="true" />
          <div className="rounded-md shadow-sm -space-y-px">
            <InputWrapper
              id="email-address"
              name="email"
              type="email"
              autoComplete="email"
              required
              className="login-input rounded-t-md"
              placeholder="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />

            <InputWrapper
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              required
              className="login-input rounded-b-md"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>

          <div className="flex items-center justify-end">
            <div className="text-sm">
              <Link
                to={match !== null ? "/admin" : "/register"}
                className="font-medium text-violet-600 hover:text-violet-500"
              >
                {match !== null ? "Forgot your password" : "Create New Account"}
              </Link>
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-violet-600 hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500"
            >
              Sign in
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};

export default Login;
